import type { ReactNode } from 'react';
import { Ban } from 'lucide-react';
import { LabelWithValue } from '@/components/common/LabelWithValue';
import { formatTaka } from '@/utils/number';
import type { PcmpProposalTeacher } from '../../types/pcmpProposal.types';
import { PcmpAmountTile } from './PcmpAmountTile';
import { PcmpProposalSection } from './PcmpProposalSection';
import { PcmpStatusPill } from './PcmpStatusPill';
import { PcmpTeacherLedgerTable } from './PcmpTeacherLedgerTable';

interface PcmpTeacherCardProps {
  teacher: PcmpProposalTeacher;
  proposedAmount: number;
  isSubmissionRejected: boolean;
  actions?: ReactNode;
}

export function PcmpTeacherCard({
  teacher,
  proposedAmount,
  isSubmissionRejected,
  actions,
}: PcmpTeacherCardProps) {
  const { balance } = teacher;
  // Legacy LDS-764: a rejected institute's agreed amount is shown but not counted.
  const isAgreedRejected = balance?.agreedRejected ?? isSubmissionRejected;
  const categoryTotal = teacher.categoryAmounts.reduce((sum, item) => sum + item.amount, 0);

  return (
    <PcmpProposalSection
      title="Teacher"
      actions={
        <>
          {teacher.isDeleted ? <PcmpStatusPill tone="danger">Removed</PcmpStatusPill> : null}
          {actions}
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <LabelWithValue label="Name">{teacher.name}</LabelWithValue>
        <LabelWithValue label="Mobile">{teacher.mobile}</LabelWithValue>
        <LabelWithValue label="Designation">{teacher.designation}</LabelWithValue>
        <LabelWithValue label="Subject">{teacher.subject}</LabelWithValue>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <PcmpAmountTile
          label="Agreed"
          value={formatTaka(teacher.agreedAmount)}
          isStruck={isAgreedRejected}
          note={
            isAgreedRejected ? (
              <span className="inline-flex items-center gap-1 text-destructive">
                <Ban className="size-3" aria-hidden />
                Institute rejected — not counted
              </span>
            ) : null
          }
        />
        <PcmpAmountTile
          label="Disbursed so far"
          value={formatTaka(balance?.disbursed)}
          tone="success"
        />
        <PcmpAmountTile label="This proposal" value={formatTaka(proposedAmount)} tone="info" />
        {balance && balance.extra > 0 ? (
          <PcmpAmountTile label="Overpaid" value={formatTaka(balance.extra)} tone="danger" />
        ) : (
          <PcmpAmountTile label="Due" value={formatTaka(balance?.due)} tone="warning" />
        )}
      </div>

      {teacher.categoryAmounts.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Agreed amount by category</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {teacher.categoryAmounts.map((item) => (
              <div
                key={item.categoryId}
                className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
              >
                <span>{item.categoryName}</span>
                <span className="tabular-nums">
                  {formatTaka(item.amount)}
                  {categoryTotal > 0 ? (
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({Math.round((item.amount / categoryTotal) * 100)}%)
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">All proposals for this teacher</h3>
        <PcmpTeacherLedgerTable ledger={teacher.ledger} />
      </div>

      {teacher.comments ? (
        <LabelWithValue label="Teacher Note">
          <span className="whitespace-pre-line font-normal">{teacher.comments}</span>
        </LabelWithValue>
      ) : null}
    </PcmpProposalSection>
  );
}
